<template>
  <div class="mt-2">
    <!-- Template -->
    <div v-if="needsTemplate()" class="mb-2">
      <label class="dtb-field-label">Template <span class="text-alert">*</span></label>
      <input v-model="localStep.config.template" class="dtb-input" placeholder="{Month} {Year} - Some Text" @input="emitChange" />
      <p class="dtb-hint">
        Use <code class="bg-surface-dark px-1 rounded text-[11px]">{ColumnName}</code> to insert row values. Example:
        <code class="bg-surface-dark px-1 rounded text-[11px]">{Quarter} {Year} Assets</code>
      </p>
      <p v-if="submitted && !localStep.config.template" class="dtb-error">Required.</p>
    </div>

    <!-- Lookup -->
    <template v-if="needsLookup()">
      <lookup-editor
        :lookup-list="localStep.config.lookupList"
        @update="
          (val) => {
            localStep.config.lookupList = val;
            emitChange();
          }
        "
      />
      <div class="mb-2">
        <label class="dtb-field-label">Default Value</label>
        <input v-model="localStep.config.defaultValue" class="dtb-input" placeholder="Fallback if no match (leave blank to keep original)" @input="emitChange" />
      </div>
    </template>

    <!-- Left / Right columns (multiply, divide, add, subtract, percentage, daysBetween) -->
    <div v-if="needsLeftRight()" class="flex gap-2 mb-2 flex-col sm:flex-row">
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Left Column <span class="text-alert">*</span></label>
        <input v-model="localStep.config.left" class="dtb-input" placeholder="Column A" @input="emitChange" />
        <p v-if="submitted && !localStep.config.left" class="dtb-error">Required.</p>
      </div>
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Right Column <span class="text-alert">*</span></label>
        <input v-model="localStep.config.right" class="dtb-input" placeholder="Column B" @input="emitChange" />
        <p v-if="submitted && !localStep.config.right" class="dtb-error">Required.</p>
      </div>
    </div>

    <!-- Replace -->
    <div v-if="needsReplace()" class="flex gap-2 mb-2 flex-col sm:flex-row">
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Search Value <span class="text-alert">*</span></label>
        <input v-model="localStep.config.searchValue" class="dtb-input" @input="emitChange" />
      </div>
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Replace With</label>
        <input v-model="localStep.config.replaceValue" class="dtb-input" @input="emitChange" />
      </div>
    </div>

    <!-- Substring -->
    <div v-if="needsSubstring()" class="flex gap-2 mb-2 flex-col sm:flex-row">
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Start Index</label>
        <input v-model.number="localStep.config.start" type="number" class="dtb-input" @input="emitChange" />
      </div>
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">End Index</label>
        <input v-model.number="localStep.config.end" type="number" class="dtb-input" @input="emitChange" />
      </div>
    </div>

    <!-- Split Part -->
    <div v-if="needsSplitPart()" class="flex gap-2 mb-2 flex-col sm:flex-row">
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Delimiter</label>
        <input v-model="localStep.config.delimiter" class="dtb-input" placeholder="e.g. ," @input="emitChange" />
      </div>
      <div class="flex-1 min-w-0">
        <label class="dtb-field-label">Part Index (0-based)</label>
        <input v-model.number="localStep.config.index" type="number" class="dtb-input" @input="emitChange" />
      </div>
    </div>

    <!-- Concat / Coalesce columns -->
    <template v-if="needsColumns()">
      <div class="mb-2">
        <label class="dtb-field-label">Columns <span class="text-alert">*</span></label>
        <tag-input
          :tags="localStep.config.columns"
          :input-value="localStep.colInput || ''"
          @update:inputValue="(val) => (localStep.colInput = val)"
          @add="$emit('add-tag', localStep, 'columns')"
          @remove="(ci) => $emit('remove-tag', localStep.config.columns, ci)"
        />
      </div>
      <div v-if="is('concat')" class="mb-2">
        <label class="dtb-field-label">Separator</label>
        <input v-model="localStep.config.separator" class="dtb-input" placeholder="e.g. , or -" @input="emitChange" />
      </div>
    </template>

    <!-- Conditional -->
    <template v-if="needsConditional()">
      <div class="flex gap-2 mb-2 flex-col sm:flex-row">
        <div class="flex-1 min-w-0">
          <label class="dtb-field-label">Operator <span class="text-alert">*</span></label>
          <select v-model="localStep.config.operation" class="dtb-select" @change="emitChange">
            <option value="" disabled>Select…</option>
            <option v-for="op in operatorOptions" :key="op.value" :value="op.value">{{ op.label }}</option>
          </select>
        </div>
        <div class="flex-1 min-w-0">
          <label class="dtb-field-label">Compare Value</label>
          <input v-model="localStep.config.value" class="dtb-input" @input="emitChange" />
        </div>
      </div>
      <div class="flex gap-2 mb-2 flex-col sm:flex-row">
        <div class="flex-1 min-w-0">
          <label class="dtb-field-label">True Value <span class="text-alert">*</span></label>
          <input v-model="localStep.config.trueValue" class="dtb-input" @input="emitChange" />
        </div>
        <div class="flex-1 min-w-0">
          <label class="dtb-field-label">False Value <span class="text-alert">*</span></label>
          <input v-model="localStep.config.falseValue" class="dtb-input" @input="emitChange" />
        </div>
      </div>
    </template>

    <!-- Round -->
    <div v-if="needsRound()" class="mb-2">
      <label class="dtb-field-label">Decimal Places</label>
      <input v-model.number="localStep.config.decimals" type="number" min="0" class="dtb-input" @input="emitChange" />
    </div>

    <!-- Constant -->
    <div v-if="needsConstant()" class="mb-2">
      <label class="dtb-field-label">Value <span class="text-alert">*</span></label>
      <input v-model="localStep.config.value" class="dtb-input" @input="emitChange" />
      <p v-if="submitted && (localStep.config.value === '' || localStep.config.value === undefined)" class="dtb-error">Required.</p>
    </div>
  </div>
</template>

<script>
import TagInput from './TagInput.vue';
import LookupEditor from './LookupEditor.vue';

export default {
  name: 'TransformFields',
  components: { LookupEditor, TagInput },
  props: {
    step: { type: Object, required: true },
    submitted: { type: Boolean, default: false },
    operatorOptions: { type: Array, default: () => [] },
    showSourceColumn: { type: Boolean, default: false },
  },
  data() {
    return {
      localStep: JSON.parse(JSON.stringify(this.step)), // deep clone to avoid reference sharing
    };
  },
  watch: {
    step: {
      deep: true,
      handler(newVal) {
        this.localStep = JSON.parse(JSON.stringify(newVal));
      },
    },
  },
  computed: {
    t() {
      return this.step.config.transform;
    },
  },
  methods: {
    is(...transforms) {
      return transforms.includes(this.t);
    },
    emitChange() {
      console.log('emitChange', this.localStep); // eslint-disable-line
      this.$emit('update:step', JSON.parse(JSON.stringify(this.localStep)));
      this.$emit('change');
    },
    needsTemplate() {
      return this.t === 'template';
    },
    needsLookup() {
      return this.t === 'lookup';
    },
    needsLeftRight() {
      return ['multiply', 'divide', 'add', 'subtract', 'percentage', 'daysBetween'].includes(this.t);
    },
    needsReplace() {
      return this.t === 'replace';
    },
    needsSubstring() {
      return this.t === 'substring';
    },
    needsSplitPart() {
      return this.t === 'splitPart';
    },
    needsColumns() {
      return ['concat', 'coalesce'].includes(this.t);
    },
    needsConditional() {
      return this.t === 'conditional';
    },
    needsRound() {
      return this.t === 'round';
    },
    needsConstant() {
      return this.t === 'constant';
    },
  },
};
</script>

<style scoped>
/* eslint-disable max-len */
/* ─── Minimal custom styles that can't be done with Tailwind alone ─── */
.dtb-select {
  @apply block w-full py-[7px] px-2 text-[13px] border border-stroke rounded-md bg-surface-light text-gray-500 outline-none transition-all duration-150 box-border;
  appearance: auto;
}
.dtb-select:focus {
  @apply border-secondary ring-2 ring-secondary/[0.18];
}
.dtb-input {
  @apply block w-full py-[7px] px-2 text-[13px] border border-stroke rounded-md bg-surface-light text-gray-500 outline-none transition-all duration-150 box-border;
}
.dtb-input:focus {
  @apply border-secondary ring-2 ring-secondary/[0.18];
}
.dtb-field-label {
  @apply block font-400 text-xs text-gray-500 mb-[3px];
}
.dtb-error {
  @apply mt-1 text-[11px] text-alert font-400;
}
.dtb-hint {
  @apply mt-1 text-[11px] text-gray-500;
}
</style>
